"use strict";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import { DELI_BASE_WHITELISTED_INCENTIVES } from "constants/morphex";
import {
  getDisplayableError,
  handleApprovalTransactionStep,
  handleOnChainStep,
  handlePermitTransactionStep,
  handleSignatureStep
} from "state/sagas/transactions/utils";
import {
  TransactionType
} from "state/transactions/types";
import invariant from "tiny-invariant";
import { call } from "typed-redux-saga";
import { generateLPTransactionSteps } from "uniswap/src/features/transactions/liquidity/steps/generateLPTransactionSteps";
import {
  DAILY_EPOCH_GAUGE_ABI,
  DAILY_EPOCH_GAUGE_ADDRESS,
  INCENTIVE_GAUGE_ABI,
  INCENTIVE_GAUGE_ADDRESS
} from "uniswap/src/features/transactions/liquidity/steps/shared";
import {
  accumulateClaimedBmx,
  buildStakeApprovalTxRequest,
  buildStakeBmxTxRequest,
  toBmxCurrencyAmount
} from "uniswap/src/features/transactions/liquidity/steps/stakeAfterClaim";
import {
  LiquidityTransactionType
} from "uniswap/src/features/transactions/liquidity/types";
import { createApprovalTransactionStep } from "uniswap/src/features/transactions/steps/approve";
import { TransactionStepType } from "uniswap/src/features/transactions/steps/types";
import { createSaga } from "uniswap/src/utils/saga";
import { logger } from "utilities/src/logger/logger";
import { currencyId } from "utils/currencyId";
import { decodeFunctionData } from "viem";
function* getLiquidityTxRequest(step, signature, prevHash) {
  if (step.type === TransactionStepType.InitializePoolTransaction || step.type === TransactionStepType.IncreasePositionTransaction || step.type === TransactionStepType.SubscribePositionTransaction || step.type === TransactionStepType.DecreasePositionTransaction || step.type === TransactionStepType.MigratePositionTransaction || step.type === TransactionStepType.CollectFeesTransactionStep || step.type === TransactionStepType.CollectLpIncentiveRewardsTransactionStep) {
    return step.txRequest;
  }
  if (step.type === TransactionStepType.SubscribePositionTransactionAsync) {
    if (!prevHash) {
      throw new Error("Previous transaction hash required for subscribe step");
    }
    const txRequest2 = yield* call(step.getTxRequest, prevHash);
    invariant(txRequest2 !== void 0, "txRequest must be defined");
    try {
      console.log("lp:subscribe:txRequest", {
        to: txRequest2.to,
        from: txRequest2.from,
        dataLen: txRequest2.data?.length,
        chainId: txRequest2.chainId
      });
    } catch {
    }
    return txRequest2;
  }
  if (!signature) {
    throw new Error("Signature required for async increase position transaction step");
  }
  const txRequest = yield* call(step.getTxRequest, signature);
  invariant(txRequest !== void 0, "txRequest must be defined");
  return txRequest;
}
function* handlePositionTransactionStep(params) {
  const { action, step, signature, analytics } = params;
  let info = step.type === TransactionStepType.InitializePoolTransaction ? {
    type: TransactionType.CREATE_V3_POOL,
    baseCurrencyId: currencyId(action.currency0Amount.currency),
    quoteCurrencyId: currencyId(action.currency1Amount.currency)
  } : getLiquidityTransactionInfo(action);
  const txRequest = yield* call(getLiquidityTxRequest, step, signature, params.prevHash);
  const onModification = ({ hash: hash2, data }) => {
    if (analytics) {
    }
  };
  const onChainStep = { ...step, txRequest };
  if (onChainStep.type === TransactionStepType.CollectLpIncentiveRewardsTransactionStep) {
    try {
      const toAddr = String(onChainStep.txRequest?.to || "").toLowerCase();
      let tokenAddress = onChainStep.rewardTokenAddress;
      if (toAddr === INCENTIVE_GAUGE_ADDRESS.toLowerCase()) {
        const decoded = decodeFunctionData({
          abi: INCENTIVE_GAUGE_ABI,
          data: onChainStep.txRequest.data
        });
        tokenAddress = decoded?.args?.[1];
      } else if (toAddr === DAILY_EPOCH_GAUGE_ADDRESS.toLowerCase()) {
        const decoded = decodeFunctionData({
          abi: DAILY_EPOCH_GAUGE_ABI,
          data: onChainStep.txRequest.data
        });
        const fn = decoded?.functionName;
        if (fn === "claimAllForOwner") {
          info = { type: TransactionType.REWARDS_COLLECTED };
          tokenAddress = void 0;
        } else {
          const bmx = Array.isArray(DELI_BASE_WHITELISTED_INCENTIVES) ? DELI_BASE_WHITELISTED_INCENTIVES.find((t) => String(t.symbol).toUpperCase() === "BMX") : void 0;
          tokenAddress = bmx?.address;
        }
      }
      if (tokenAddress) {
        info = { type: TransactionType.LP_INCENTIVES_CLAIM_REWARDS, tokenAddress };
      }
    } catch {
    }
  }
  if (onChainStep.type === TransactionStepType.SubscribePositionTransaction) {
    info = { type: TransactionType.SUBSCRIBE_POSITION };
  }
  const shouldWait = onChainStep.type === TransactionStepType.SubscribePositionTransaction || onChainStep.type === TransactionStepType.SubscribePositionTransactionAsync || onChainStep.type === TransactionStepType.CollectLpIncentiveRewardsTransactionStep;
  const hash = yield* call(handleOnChainStep, {
    ...params,
    info,
    step: onChainStep,
    allowDuplicativeTx: onChainStep.type === TransactionStepType.SubscribePositionTransaction || onChainStep.type === TransactionStepType.SubscribePositionTransactionAsync || onChainStep.type === TransactionStepType.CollectLpIncentiveRewardsTransactionStep,
    shouldWaitForConfirmation: shouldWait,
    onModification
  });
  if (analytics) {
  }
  popupRegistry.addPopup({ type: PopupType.Transaction, hash }, hash);
  return hash;
}
function* modifyLiquidity(params) {
  const {
    account,
    setCurrentStep,
    steps: initialSteps,
    liquidityTxContext: { action },
    onSuccess,
    onFailure,
    analytics
  } = params;
  let steps = initialSteps;
  let signature;
  let lastHash;
  let lastSuccessfulStepType;
  for (const step of [...steps]) {
    try {
      logger.info("liquiditySaga", "modifyLiquidity", "executing step", { type: step.type });
      console.log("lp:step:execute", step.type);
      if (step.type === TransactionStepType.SubscribePositionTransactionAsync) {
        console.log("lp:step:lastHashBeforeSubscribe", {
          lastHash,
          lastHashLength: lastHash?.length
        });
      }
    } catch {
    }
    try {
      switch (step.type) {
        case TransactionStepType.TokenRevocationTransaction:
        case TransactionStepType.TokenApprovalTransaction: {
          yield* call(handleApprovalTransactionStep, { account, step, setCurrentStep });
          lastSuccessfulStepType = step.type;
          break;
        }
        case TransactionStepType.Permit2Signature: {
          signature = yield* call(handleSignatureStep, { account, step, setCurrentStep });
          lastSuccessfulStepType = step.type;
          break;
        }
        case TransactionStepType.Permit2Transaction: {
          yield* call(handlePermitTransactionStep, { account, step, setCurrentStep });
          lastSuccessfulStepType = step.type;
          break;
        }
        case TransactionStepType.InitializePoolTransaction:
        case TransactionStepType.IncreasePositionTransaction:
        case TransactionStepType.IncreasePositionTransactionAsync:
        case TransactionStepType.SubscribePositionTransaction:
        case TransactionStepType.SubscribePositionTransactionAsync:
        case TransactionStepType.DecreasePositionTransaction:
        case TransactionStepType.MigratePositionTransaction:
        case TransactionStepType.MigratePositionTransactionAsync:
        case TransactionStepType.CollectFeesTransactionStep:
        case TransactionStepType.CollectLpIncentiveRewardsTransactionStep:
          {
            const hash = yield* call(handlePositionTransactionStep, {
              account,
              step,
              setCurrentStep,
              action,
              signature,
              analytics,
              prevHash: lastHash
            });
            if (hash) lastHash = hash;
            if (step.type === TransactionStepType.CollectLpIncentiveRewardsTransactionStep && step.stakeAfterClaimAccumulator && step.isBmxReward && step.stakeBmxToken) {
              yield* call(accumulateClaimedBmx, {
                chainId: step.txRequest.chainId,
                hash,
                rewardTokenAddress: step.rewardTokenAddress,
                accumulator: step.stakeAfterClaimAccumulator,
                gauge: step.gauge,
                bmxToken: step.stakeBmxToken
              });
              params.setSteps([...steps]);
            }
            try {
              logger.info("liquiditySaga", "modifyLiquidity", "step hash", { type: step.type, hash: lastHash });
              console.log("lp:step:hash", step.type, lastHash);
            } catch {
            }
            lastSuccessfulStepType = step.type;
            if (step.type === TransactionStepType.IncreasePositionTransactionAsync) {
              signature = void 0;
            }
          }
          break;
        case TransactionStepType.StakeBmxApprovalTransaction: {
          const stakeStep = step;
          const totalClaimed = stakeStep.config.accumulator.totalClaimed;
          if (totalClaimed <= BigInt(0)) {
            steps = steps.filter((candidate) => candidate !== step);
            params.setSteps([...steps]);
            lastSuccessfulStepType = step.type;
            break;
          }
          const approvalTx = yield* call(buildStakeApprovalTxRequest, stakeStep.config, totalClaimed);
          if (!approvalTx) {
            steps = steps.filter((candidate) => candidate !== step);
            params.setSteps([...steps]);
            lastSuccessfulStepType = step.type;
            break;
          }
          const approvalAmount = toBmxCurrencyAmount(stakeStep.config.bmxToken, totalClaimed);
          const approvalStep = createApprovalTransactionStep(approvalTx, approvalAmount);
          if (!approvalStep) {
            steps = steps.filter((candidate) => candidate !== step);
            params.setSteps([...steps]);
            lastSuccessfulStepType = step.type;
            break;
          }
          const mapCurrentStep = ({ accepted }) => setCurrentStep({ step: stakeStep, accepted });
          yield* call(handleApprovalTransactionStep, {
            account,
            step: approvalStep,
            setCurrentStep: mapCurrentStep
          });
          lastSuccessfulStepType = step.type;
          break;
        }
        case TransactionStepType.StakeBmxTransaction: {
          const stakeStep = step;
          const totalClaimed = stakeStep.config.accumulator.totalClaimed;
          if (totalClaimed <= BigInt(0)) {
            lastSuccessfulStepType = step.type;
            break;
          }
          const stakeTx = buildStakeBmxTxRequest(stakeStep.config, totalClaimed);
          if (!stakeTx) {
            steps = steps.filter((candidate) => candidate !== step);
            params.setSteps([...steps]);
            lastSuccessfulStepType = step.type;
            break;
          }
          const info = {
            type: TransactionType.CLAIM_AND_STAKE,
            tokenAddress: stakeStep.config.bmxToken.address,
            amountRaw: totalClaimed.toString()
          };
          const mapCurrentStep = ({ accepted }) => setCurrentStep({ step: stakeStep, accepted });
          const hash = yield* call(handleOnChainStep, {
            account,
            step: { ...stakeStep, txRequest: stakeTx },
            info,
            setCurrentStep: mapCurrentStep
          });
          if (hash) {
            lastHash = hash;
          }
          lastSuccessfulStepType = step.type;
          break;
        }
        default: {
          throw new Error("Unexpected step type");
        }
      }
    } catch (e) {
      if (step.type === TransactionStepType.CollectLpIncentiveRewardsTransactionStep) {
        try {
          const claimStep = step;
          const request = claimStep.txRequest;
          logger.warn("liquiditySaga", "modifyLiquidity", "claim step failure context", {
            chainId: request?.chainId,
            to: request?.to,
            data: request?.data,
            value: request?.value,
            gauge: claimStep.gauge,
            rewardSymbol: claimStep.rewardSymbol,
            accumulator: claimStep.stakeAfterClaimAccumulator
          });
        } catch {
        }
      }
      const displayableError = getDisplayableError(e, step, "liquidity");
      if (displayableError) {
        logger.error(displayableError, { tags: { file: "liquiditySaga", function: "modifyLiquidity" } });
      }
      onFailure({
        failedStepType: step.type,
        lastSuccessfulStepType,
        lastTransactionHash: lastHash
      });
      return;
    }
  }
  yield* call(onSuccess);
}
function* liquidity(params) {
  const { liquidityTxContext, startChainId, selectChain, onFailure, account, requireChainSwitch } = params;
  let steps = yield* call(generateLPTransactionSteps, liquidityTxContext);
  params.setSteps(steps);
  try {
    logger.info("liquiditySaga", "liquidity", "generated steps", { types: steps.map((s) => s.type) });
    console.log(
      "lp:steps",
      steps.map((s) => s.type)
    );
  } catch {
  }
  const token0ChainId = liquidityTxContext.action.currency0Amount.currency.chainId;
  const token1ChainId = liquidityTxContext.action.currency1Amount.currency.chainId;
  if (token0ChainId !== token1ChainId) {
    logger.error("Tokens must be on the same chain", {
      tags: { file: "liquiditySaga", function: "liquidity" }
    });
    onFailure();
    return void 0;
  }
  if (requireChainSwitch && token0ChainId !== account.chainId) {
    yield* call(selectChain, token0ChainId);
  } else if (token0ChainId !== startChainId) {
    const chainSwitched = yield* call(selectChain, token0ChainId);
    if (!chainSwitched) {
      onFailure();
      return void 0;
    }
  }
  return yield* modifyLiquidity({
    ...params,
    steps
  });
}
export const liquiditySaga = createSaga(liquidity, "liquiditySaga");
function getLiquidityTransactionInfo(action) {
  let type;
  switch (action.type) {
    case LiquidityTransactionType.Create:
      type = TransactionType.CREATE_POSITION;
      break;
    case LiquidityTransactionType.Increase:
      type = TransactionType.INCREASE_LIQUIDITY;
      break;
    case LiquidityTransactionType.Decrease:
      type = TransactionType.DECREASE_LIQUIDITY;
      break;
    case LiquidityTransactionType.Migrate:
      type = TransactionType.MIGRATE_LIQUIDITY_V3_TO_V4;
      break;
    case LiquidityTransactionType.Collect:
      type = TransactionType.COLLECT_FEES;
      break;
    case LiquidityTransactionType.ClaimIncentives:
      type = TransactionType.COLLECT_FEES;
      break;
    case LiquidityTransactionType.Subscribe:
      type = TransactionType.INCREASE_LIQUIDITY;
      break;
  }
  const {
    currency0Amount: { currency: currency0, quotient: quotient0 },
    currency1Amount: { currency: currency1, quotient: quotient1 }
  } = action;
  return {
    type,
    token0CurrencyId: currencyId(currency0),
    token1CurrencyId: currencyId(currency1),
    token0CurrencyAmountRaw: quotient0.toString(),
    token1CurrencyAmountRaw: quotient1.toString()
  };
}
